import { type Translated } from '@myre/common-i18n';
import { AgentMemoryApi } from '@myre/app-agent-generated/sdk/angular/AgentMemoryApi';
import { type UserMemory } from '@myre/app-agent-generated/types/UserMemory';
import { type SystemI18n, type UiLibI18n } from '@myre/domain-i18n-wordings';
import {
  ElementText,
  QuestionTextbox,
  type ElementBase,
} from '@myre/ui-primitive-forms';
import { ModalService } from '@myre/ui-primitive-modal';

import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, of, type Observable } from 'rxjs';

import { WORDINGS } from '../../myre-wordings-loader';

/** The blocks a user may read and edit, in the order they are shown. */
const USER_MEMORY_BLOCKS = ['preferences', 'instructions'] as const;

type UserMemoryBlock = (typeof USER_MEMORY_BLOCKS)[number];

/** What the form hands back: one text per block, empty when erased. */
type UserMemoryValues = Record<UserMemoryBlock, string | null>;

/**
 * The chat window pins itself above the app's notification layer
 * (chat-window.component.scss: z-index 10000001); the modal must clear it
 * too or it opens underneath the chat.
 */
const ABOVE_CHAT_Z_INDEX = 10000002;

/**
 * What MYRE remembers about the connected user across conversations, as a
 * form in a modal: one text per block. The agent writes here as it talks; the
 * user reads, corrects or erases. Nothing else of the memory is shown — a
 * conversation's own memory dies with it.
 */
@Injectable({ providedIn: 'root' })
export class AgentMemoryModalService {
  private readonly _modalService = inject(ModalService);
  private readonly _agentMemoryApi = inject(AgentMemoryApi);
  private readonly _wordings = inject<SystemI18n & UiLibI18n>(WORDINGS);

  public open(customerId: number): void {
    const elements$ = new BehaviorSubject<ElementBase[]>([]);
    let stored: UserMemory = {};

    this._agentMemoryApi.getUserMemory({ customerId }).subscribe(memory => {
      stored = memory;
      elements$.next(this._elements(memory));
    });

    this._modalService.openFactory(uid => ({
      key: 'agent-memory.edit',
      size: 'small',
      zIndex: ABOVE_CHAT_Z_INDEX,
      header: { data: { title: this._wordings._library.chat.memory() } },
      context: {
        key: 'agent-memory',
        elements: elements$,
        onSubmit: (values: UserMemoryValues) => {
          this._save(customerId, stored, values).subscribe(() => {
            this._modalService.close(uid);
          });
        },
        cancelClick: () => {
          this._modalService.close(uid);
        },
      },
      footer: {
        data: {
          ok: this._wordings._system.buttonSave(),
          cancel: this._wordings._system.buttonCancel(),
        },
      },
    }));
  }

  private _elements(memory: UserMemory): ElementBase[] {
    const chat = this._wordings._library.chat;
    const texts: Record<
      UserMemoryBlock,
      { label: Translated; placeholder: Translated }
    > = {
      preferences: {
        label: chat.memoryPreferences(),
        placeholder: chat.memoryPreferencesHint(),
      },
      instructions: {
        label: chat.memoryInstructions(),
        placeholder: chat.memoryInstructionsHint(),
      },
    };

    return [
      new ElementText({ key: 'description', text: chat.memoryDescription() }),
      ...USER_MEMORY_BLOCKS.map(
        block =>
          new QuestionTextbox<string>({
            key: block,
            type: 'textarea',
            rows: 3,
            label: texts[block].label,
            placeholder: texts[block].placeholder,
            initialValue: memory[block] ?? '',
          }),
      ),
    ];
  }

  /** Write the blocks whose text changed; an emptied block is erased. */
  private _save(
    customerId: number,
    stored: UserMemory,
    values: UserMemoryValues,
  ): Observable<unknown> {
    const writes = USER_MEMORY_BLOCKS.filter(
      block => (values[block] ?? '') !== (stored[block] ?? ''),
    ).map(block =>
      this._agentMemoryApi.updateUserMemoryBlock(
        { block, customerId },
        { content: values[block] || null },
      ),
    );

    return writes.length ? forkJoin(writes) : of(undefined);
  }
}
